import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, Subject } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'
import { HealthInsuranceType } from '../interfaces/health-insurance'
import { InsurancePlanType } from '../interfaces/insurance-plan'
import { ParameterType } from '../interfaces/parameter'
import { ServerUrlService } from './server-url.service'

@Injectable({
  providedIn: 'root',
})
export class ParametersService {
  private parameters: ParameterType[] = []
  private useWebCam = false
  private patientParamsRegister: any
  private paramsProc: any
  private healthInsurance: HealthInsuranceType[] = []
  private insurancePlan: InsurancePlanType[] = []
  private headers: any
  private firstLoad: boolean = false
  public appearanceEnabled: string = 'false'
  private subjectFullScreen = new Subject<any>()

  constructor(private serverUrl: ServerUrlService, private http: HttpClient) {
    this.headers = {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  }

  public init(): void {
    this.startParameters()
  }

  public load(params: string[]): Observable<any> {
    return this.http.post(
      `${this.serverUrl.getUrl()}/parametros/get`,
      JSON.stringify({
        chaves: params,
      }),
      this.headers
    )
  }

  private startParameters(): void {
    if (this.firstLoad === false) {
      this.loadDefaultParams.pipe(map((res) => {}))
    }
  }

  get loadDefaultParams(): Observable<any> {
    const params = [
      'SPEECH_NAME_PANEL',
      'OTIMUS_MIDIA_URL',
      'APP_VET',
      'TOTEM_CALENDAR',
      'SPEECH_PREV_PANEL',
      'PRIORITY',
      'SPEECH_LOCAL_ROOM',
      'TOTEM',
      'TOTEM_PRINTER',
      'USAR_LOCAL',
      'MESSAGE_AFTER_CHECKIN',
      'EXIBITION_TYPE',
      'TOTEM_CHECKIN_CHANGES_STATUS',
    ]

    if (this.appearanceEnabled === 'true') {
      return this.hasAppearanceParameters.pipe(
        tap((resp) => {
          params.push(
            'TEXT_FONT_FAMILY',
            'TEXT_COLOR',
            'PANEL_CLOCK_TEXT_COLOR',
            'PANEL_CLOCK_BACKGROUND_COLOR',
            'PANEL_LOGO_IMAGE_NAME',
            'PANEL_LOGO_IMAGE_URL',
            'TOTEM_BACKGROUND_TYPE',
            'TOTEM_BACKGROUND_IMAGE_NAME',
            'TOTEM_BACKGROUND_IMAGE_URL',
            'TOTEM_BACKGROUND_COLOR_PRIMARY',
            'TOTEM_BACKGROUND_COLOR_SECONDARY'
          )
        }),
        switchMap(() =>
          this.load(params).pipe(
            tap((response) => {
              this.firstLoad = true
              this.parameters = response
            })
          )
        )
      )
    } else {
      return this.load(params).pipe(
        tap((response) => {
          this.firstLoad = true
          this.parameters = response
        })
      )
    }
  }

  public getParams(): ParameterType[] {
    return this.parameters
  }

  public getParam(key: string): ParameterType {
    const param = this.parameters.find((item) => item.chave === key) || ([] as ParameterType)
    return param
  }

  public waitGetParam(key: string): Promise<ParameterType> {
    const promise = new Promise<ParameterType>((resolve, reject) => {
      setTimeout(() => {
        const param = this.parameters.find((item) => item.chave === key) || ([] as ParameterType)
        resolve(param)
      }, 10000)
    })
    return promise
  }

  public setUseCam(useCam: boolean): void {
    this.useWebCam = useCam
  }

  public getUseCam(): boolean {
    return this.useWebCam
  }

  public getTotemParams(pacienteid: string): void {
    this.http.get(`${this.serverUrl.getUrl()}/usuarios/params_atende/${pacienteid}`).subscribe(
      (resp: any) => {
        this.patientParamsRegister = resp.exigeNoCadastro
        this.paramsProc = resp.exigeNoProc
        this.healthInsurance = resp.listaConvenios as HealthInsuranceType[]
        this.insurancePlan = resp.listaPlanos as InsurancePlanType[]
        this.orderHelthPlan()
      },
      (err) => console.error('err', err)
    )
  }

  public getHealthInsurance(): HealthInsuranceType[] {
    return this.healthInsurance
  }

  public getInsurancePlan(): InsurancePlanType[] {
    return this.insurancePlan
  }

  private orderHelthPlan(): void {
    this.healthInsurance = this.healthInsurance.filter((item) => item.CategoriaPlanoSituacao === 1)
    this.healthInsurance.sort((a, b) =>
      (a.CategoriaCatNOME || '') > (b.CategoriaCatNOME || '') ? 1 : -1
    )
    this.insurancePlan = this.insurancePlan.filter((item) => item.PlanosMultiplosStatus === 1)
    this.insurancePlan.sort((a, b) =>
      (a.PlanosMultiplosNome || '') > (b.PlanosMultiplosNome || '') ? 1 : -1
    )
  }

  public isFirstLoad(): boolean {
    return this.firstLoad
  }

  public updateParams(params: any) {
    this.http
      .put(
        `${this.serverUrl.getUrl()}/parametros/multi`,
        JSON.stringify(this.getParamsList(params)),
        this.headers
      )
      .subscribe((res) => {})
  }

  public getParamsList(params: any) {
    var listParams = []
    for (const property in params) {
      let param = { chave: property, valor: params[property] }
      listParams.push(param)
    }
    return listParams
  }

  public getVarSys(params: string): Observable<any> {
    return this.http.post(
      `${this.serverUrl.getUrl()}/parametros/varsys/get`,
      JSON.stringify({
        keys: params,
      }),
      this.headers
    )
  }

  get hasAppearanceParameters() {
    return this.http.post(
      `${this.serverUrl.getUrl()}/parametros/check/appearance`,
      {},
      this.headers
    )
  }

  public uploadFile(formData: FormData): Observable<any> {
    return this.http.post(
      `${this.serverUrl.otimusClinicUrl}/files/uploads/at/selfserviceappearance`,
      formData
    )
  }

  public listenFullScreen(): Observable<any> {
    return this.subjectFullScreen.asObservable()
  }

  public callFullScreen(): void {
    this.subjectFullScreen.next(true)
  }
}
